/**
 * Move a document to another folder — the keyboard-and-click path to what a
 * drop does. Picking a folder hands it to the placement controller, so the
 * choice means exactly what dropping on that folder would. Folders the file
 * can't go to (Arche Saves, My-Fias) are shown but disabled, with the same
 * reason a drop there would give — the pure `planDocumentPlacement` decides
 * both, and the server decides again.
 */
import { useEffect, useMemo, useState } from 'react';
import { Folder as FolderIcon, Inbox, Lock } from 'lucide-react';
import type { VaultDocumentDto, VaultDocumentFolderDto } from '@/lib/api';
import { planDocumentPlacement } from '../../lib/placement-plan';
import { describeBlockedPlacement, describeCopyPlacement } from '../DocumentPlacement';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

export interface MoveDocumentDialogProps {
  document: VaultDocumentDto | null;
  folders: readonly VaultDocumentFolderDto[];
  /** The page's placement controller — the same one the drop targets use, so
   *  picking a folder here means exactly what dropping on it would. */
  onPlace: (
    documentId: string,
    targetFolderId: string | null,
    known: VaultDocumentDto,
  ) => Promise<unknown>;
  onClose: () => void;
}

export function MoveDocumentDialog({
  document,
  folders,
  onPlace,
  onClose,
}: MoveDocumentDialogProps) {
  // Stage the target picker selection separately from the doc's current
  // folder so the "Move" button is only enabled when the user actually
  // picks somewhere else. `null` = Holder root.
  const [target, setTarget] = useState<string | null>(null);

  // Reset the staged selection whenever a new document opens the dialog.
  useEffect(() => {
    if (document) setTarget(document.folderId ?? null);
  }, [document]);

  const open = document !== null;
  const currentFolderId = document?.folderId ?? null;
  const unchanged = target === currentFolderId;
  const pathOf = (folderId: string | null) =>
    folderId === null ? null : (folders.find((f) => f.folderId === folderId)?.path ?? null);
  const planFor = (folderId: string | null) =>
    planDocumentPlacement(
      { folderId: currentFolderId, folderPath: pathOf(currentFolderId) },
      { folderId, path: pathOf(folderId) },
    );
  /** Why the file can't go to `folderId`, or null when it can. */
  const refusalFor = (folderId: string | null): string | null => {
    if (!document) return null;
    const plan = planFor(folderId);
    return plan.kind === 'blocked' ? describeBlockedPlacement(plan, document.name, folders) : null;
  };
  const targetRefusal = unchanged ? null : refusalFor(target);
  // An email attachment is copied, not moved (the placement asks first).
  const copies = !!document && !unchanged && planFor(target).kind === 'copy';

  // Sort folders by path so /My-Fias/* clusters together, with user
  // folders interleaved alphabetically. Path is already denormalized
  // server-side, so we just compare strings.
  const sorted = useMemo(
    () => [...folders].sort((a, b) => a.path.localeCompare(b.path)),
    [folders],
  );

  const handleMove = async () => {
    if (!document || unchanged) return;
    if (targetRefusal) return;
    // Close first: outcomes (a refusal, a failed move) are reported by the
    // page's placement banner, not by this dialog.
    onClose();
    await onPlace(document.documentId, target, document);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Move document</DialogTitle>
          <DialogDescription>
            Pick a folder in your Documents for "{document?.name}". Arche Saves and My-Fias don’t
            accept files.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-72 overflow-y-auto rounded border border-border bg-card">
          <button
            type="button"
            onClick={() => setTarget(null)}
            className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
              target === null ? 'bg-primary/10 font-medium' : 'hover:bg-muted/40'
            }`}
          >
            <Inbox className="h-4 w-4 text-muted-foreground" />
            <span>Documents</span>
            {currentFolderId === null && (
              <span className="ml-auto text-xs text-muted-foreground">current</span>
            )}
          </button>
          {sorted.map((f) => {
            const refusal = currentFolderId === f.folderId ? null : refusalFor(f.folderId);
            return (
              <button
                key={f.folderId}
                type="button"
                disabled={refusal !== null}
                title={refusal ?? undefined}
                onClick={() => setTarget(f.folderId)}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm disabled:cursor-not-allowed disabled:opacity-50 ${
                  target === f.folderId ? 'bg-primary/10 font-medium' : 'hover:bg-muted/40'
                }`}
              >
                {f.isProtected ? (
                  <Lock className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <FolderIcon className="h-4 w-4 text-muted-foreground" />
                )}
                <span className="truncate">{f.path}</span>
                {currentFolderId === f.folderId && (
                  <span className="ml-auto text-xs text-muted-foreground">current</span>
                )}
              </button>
            );
          })}
        </div>

        {copies && document && (
          <p className="text-sm text-muted-foreground" role="note">
            {describeCopyPlacement(document.name)}
          </p>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted/40"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={unchanged || targetRefusal !== null}
            onClick={handleMove}
            className="rounded bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
          >
            {copies ? 'Copy' : 'Move'}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
